import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';
import Chart from 'src/components/chart/chart';
import useChart from 'src/components/chart/use-chart';

// Used per-series when a consumer omits `color` — falls back PER SERIES so one
// missing colour doesn't silently discard everyone else's (status-palette
// lock-step with the table chips must survive).
const FALLBACK_PALETTE = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#64748b', '#ef4444', '#06b6d4'];

const fillConfig = (isBar, seriesCount) => {
    if (isBar) return { type: 'solid', opacity: 0.9 };
    if (seriesCount > 1) return { type: 'solid', opacity: 0 };
    return { type: 'gradient', gradient: { opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100] } };
};

// 1,234,567 → "1.2m", 12,345 → "12k", 950 → "950"
const abbreviate = (v) => {
    const n = Number(v) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}m`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
    return Math.round(n).toLocaleString();
};

/**
 * Reusable line / bar chart for the analytics report tables' "Graph" view.
 *
 * Feed it the SAME data the table already renders:
 *   - `categories`: x-axis labels (e.g. month headers, source names)
 *   - `series`: [{ name, data: number[], color? }] — one entry per line/bar group
 *
 * Built-ins (all opt-out/opt-in via props, defaults preserve old behaviour):
 *   - Line/Bar switch (top-right); `type` sets the initial mode
 *   - Top-N category cap with an "Other" rollup + caption (`maxCategories`)
 *   - `horizontal` bars (long category names), `stacked` bars (consumer opts in
 *     ONLY where statuses are mutually exclusive — stacking overlapping pipeline
 *     counts would fabricate totals)
 *   - Legend shows each series' total; tooltip can append % of series total
 *   - `yAxisTitle` + abbreviated axis numbers (`axisValueFormatter` overrides)
 */
export default function MetricLineChart({
    categories, series, type, height, valueFormatter,
    allowTypeToggle, maxCategories, horizontal, stacked,
    yAxisTitle, axisValueFormatter, showPercentOfTotal,
}) {
    const [chartType, setChartType] = useState(type);
    // Keep in sync if the consumer changes `type` (derived-state guard).
    useEffect(() => { setChartType(type); }, [type]);

    const isBar = chartType === 'bar';

    // Top-N cap: rank categories by their total across all series (input order
    // is table order, which the user may have re-sorted), keep the top N-1 in
    // their original relative order, roll the rest into "Other".
    const { cappedCategories, cappedSeries, otherCount } = useMemo(() => {
        if (!maxCategories || categories.length <= maxCategories) {
            return { cappedCategories: categories, cappedSeries: series, otherCount: 0 };
        }
        const totals = categories.map((_, i) => series.reduce((s, sr) => s + (Number(sr.data?.[i]) || 0), 0));
        const order = totals.map((t, i) => [t, i]).sort((a, b) => b[0] - a[0]).map(([, i]) => i);
        const keep = order.slice(0, maxCategories - 1).sort((a, b) => a - b);
        const rest = order.slice(maxCategories - 1);
        return {
            cappedCategories: [...keep.map((i) => categories[i]), `Other (${rest.length})`],
            cappedSeries: series.map((sr) => ({
                ...sr,
                data: [...keep.map((i) => Number(sr.data?.[i]) || 0), rest.reduce((s, i) => s + (Number(sr.data?.[i]) || 0), 0)],
            })),
            otherCount: rest.length,
        };
    }, [categories, series, maxCategories]);

    const hasData = cappedCategories.length > 0 && cappedSeries.some((s) => (s.data || []).some((v) => v != null));

    const fmtValue = (v) => (valueFormatter ? valueFormatter(v) : Number(v).toLocaleString());
    const fmtAxis = axisValueFormatter || abbreviate;
    const isHorizontal = isBar && horizontal;
    // Horizontal charts grow with the category count so labels never overlap.
    const chartHeight = isHorizontal ? Math.max(height, cappedCategories.length * 32 + 80) : height;
    // % of total is only honest against the series' OWN total across categories
    // (never a per-category sum — series can be overlapping pipeline stages).
    const seriesTotals = cappedSeries.map((s) => (s.data || []).reduce((sum, v) => sum + (Number(v) || 0), 0));
    const showPct = showPercentOfTotal == null ? (isBar && cappedSeries.length === 1) : showPercentOfTotal;

    const options = useChart({
        colors: cappedSeries.map((s, i) => s.color || FALLBACK_PALETTE[i % FALLBACK_PALETTE.length]),
        chart: { toolbar: { show: false }, zoom: { enabled: false }, stacked: isBar && stacked },
        xaxis: {
            categories: cappedCategories,
            labels: {
                style: { fontSize: '11px' },
                trim: true,
                hideOverlappingLabels: true,
                maxHeight: 96,
                ...(isHorizontal ? { formatter: (val) => fmtAxis(val) } : {}),
            },
            axisBorder: { show: false },
            // Label thinning is a time-series tool; on categorical bars it hides
            // bar identities (the cap solves the real problem there).
            tickAmount: !isBar && cappedCategories.length > 12 ? 12 : undefined,
        },
        yaxis: isHorizontal
            ? { labels: { style: { fontSize: '11px' } } }
            : {
                ...(yAxisTitle ? { title: { text: yAxisTitle, style: { fontSize: '12px', fontWeight: 500 } } } : {}),
                labels: { formatter: (v) => fmtAxis(v) },
            },
        stroke: { width: isBar ? 0 : 2.5, curve: 'smooth' },
        // Bars: solid. Single line: soft gradient wash. Multi-line: clean lines
        // only — overlapping gradient washes muddy the lower series (same fix
        // DailyMetricsChart uses).
        fill: fillConfig(isBar, cappedSeries.length),
        plotOptions: { bar: { columnWidth: stacked ? '45%' : '60%', borderRadius: 4, horizontal: isHorizontal, barHeight: '60%' } },
        legend: {
            show: cappedSeries.length > 1,
            position: 'top',
            horizontalAlign: 'right',
            // Totals in the legend: read values without hue-matching bars.
            formatter: (name, opts) => {
                const total = seriesTotals[opts?.seriesIndex] ?? 0;
                return `${name} (${fmtValue(total)})`;
            },
        },
        tooltip: {
            shared: !isBar || cappedSeries.length > 1,
            intersect: false,
            y: {
                formatter: (val, opts) => {
                    const base = fmtValue(val);
                    if (!showPct) return base;
                    const total = seriesTotals[opts?.seriesIndex] ?? 0;
                    return total > 0 ? `${base} (${((val / total) * 100).toFixed(1)}%)` : base;
                },
            },
        },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 3 },
        dataLabels: { enabled: false },
    });

    if (!hasData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <Typography sx={{ color: 'text.secondary', fontSize: '14px' }}>
                    No data to chart for the selected period.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {allowTypeToggle && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <ToggleButtonGroup
                        value={chartType}
                        exclusive
                        size="small"
                        onChange={(e, val) => { if (val) setChartType(val); }}
                        sx={{
                            '& .MuiToggleButton-root': {
                                textTransform: 'none', fontSize: '11px', fontWeight: 500,
                                px: 1.25, py: 0.25, border: '1px solid #e2e8f0',
                                '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                            },
                        }}
                    >
                        <Tooltip title="Line" arrow>
                            <ToggleButton value="line"><Iconify icon="eva:trending-up-outline" width={14} /></ToggleButton>
                        </Tooltip>
                        <Tooltip title="Bar" arrow>
                            <ToggleButton value="bar"><Iconify icon="eva:bar-chart-outline" width={14} /></ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </Box>
            )}
            <Chart
                type={isBar ? 'bar' : 'area'}
                series={cappedSeries.map((s) => ({ name: s.name, data: s.data }))}
                options={options}
                height={chartHeight}
            />
            {otherCount > 0 && (
                <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 0.5, textAlign: 'center' }}>
                    Showing top {cappedCategories.length - 1} of {categories.length}; the remaining {otherCount} are grouped as &ldquo;Other&rdquo;.
                </Typography>
            )}
        </Box>
    );
}

MetricLineChart.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.string).isRequired,
    series: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number),
        color: PropTypes.string,
    })).isRequired,
    type: PropTypes.oneOf(['line', 'bar']),
    height: PropTypes.number,
    valueFormatter: PropTypes.func,
    allowTypeToggle: PropTypes.bool,
    // Cap categorical width: keep top N-1 by total, roll the rest into "Other".
    // null disables.
    maxCategories: PropTypes.number,
    horizontal: PropTypes.bool,
    // Opt-in ONLY where series are mutually exclusive per category.
    stacked: PropTypes.bool,
    yAxisTitle: PropTypes.string,
    axisValueFormatter: PropTypes.func,
    // null = auto (on for single-series bars).
    showPercentOfTotal: PropTypes.bool,
};

MetricLineChart.defaultProps = {
    type: 'line',
    height: 340,
    valueFormatter: null,
    allowTypeToggle: true,
    maxCategories: 12,
    horizontal: false,
    stacked: false,
    yAxisTitle: null,
    axisValueFormatter: null,
    showPercentOfTotal: null,
};
